import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/lib/site";
import api from "@/lib/api";

function Counter({ label, value }) {
  return (
    <div className="text-center sm:text-left">
      <div className="font-display text-2xl lg:text-3xl gold-gradient-text">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mt-1">{label}</div>
    </div>
  );
}

export default function Hero({ onOpenChat }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/company/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <section id="top" className="relative min-h-screen flex items-center royal-radial grain overflow-hidden pt-28 pb-16">
      <div
        className="hero-photo absolute inset-0 opacity-[0.14] bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1751556602272-47b5c157da8e?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
      />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/5 px-4 py-1.5 mb-8">
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-gold">Est. {COMPANY.established} • AI Holding</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-emerald-50 leading-[1.05]">
            Enduring Intelligence,
            <span className="block gold-gradient-text mt-2">Held With Precision.</span>
          </h1>

          <p className="mt-7 text-base lg:text-lg text-slate-300 max-w-xl leading-relaxed font-light">
            {COMPANY.name} is a fully AI-operated holding company — stewarding the
            <span className="text-emerald-300"> Luchii</span> and
            <span className="text-emerald-300"> Frasberg</span> AI model families and the intelligent
            products built upon them.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href="#portfolio">
              <Button
                data-testid="hero-explore-assets-button"
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-full h-12 px-7 tracking-wide hover-emerald-glow border border-emerald-400/30"
              >
                Explore AI Assets <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Button
              onClick={onOpenChat}
              data-testid="hero-open-assistant-button"
              className="bg-transparent border border-gold/50 text-gold hover:bg-gold hover:text-emerald-950 rounded-full h-12 px-7 tracking-wide transition-all"
            >
              <MessageSquare className="w-4 h-4 mr-2" /> Speak to Luchii
            </Button>
          </div>

          {stats && (
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-8 border-t border-emerald-900/50 pt-8">
              <Counter label="Model Assets" value={stats.assets} />
              <Counter label="Training Tokens" value={stats.training_tokens} />
              <Counter label="Model Tiers" value={stats.model_tiers} />
              <Counter label="Est. Valuation" value={stats.valuation} />
            </div>
          )}
        </div>

        <div className="lg:col-span-5 flex justify-center animate-fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative">
            <div className="absolute inset-0 bg-emerald-500/10 blur-[90px] rounded-full scale-90" />
            <img
              src={COMPANY.logo}
              alt="Jahzara Phenix Jae Orion crest"
              className="relative w-72 h-72 lg:w-[26rem] lg:h-[26rem] object-contain animate-float"
              style={{ filter: "drop-shadow(0 20px 45px rgba(0,0,0,0.55))" }}
              data-testid="hero-crest"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
